import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader } from "reactstrap";
import classNames from "classnames";
import NTable from "../../../components/NTable";
import Select from "../../../components/Inputs/Select";
import ColumnToggle from "../../../components/NTable/Actions/ColumnToggle";
import Filters from "../../../components/NTable/Actions/Filters";
import { useNewTable } from "../../../components/NTable/hook";
import { useColumns } from "./columns";
import { useCustomFields } from "../../Settings/CustomFields/context";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import { useApproveDenyModal } from "./ApproveDenyModal/useApproveDenyModal";
import { useDetailsModal } from "./DetailsModal/useDetailsModal";
import { useQueryFilters } from "../../Settings/helpers/useQueryFilters";
import { useColumnFiltersConfig } from "../../../components/NTable/hook/useColumnFiltersConfig";
import { useRetroApi } from "./api";
import { formatParams } from "../../../utils/api/format";

const status = ["pending", "approved", "applied", "rejected"];

function RetroAdjustment() {
    const { t } = useTranslation();
    const [selected, setSelected] = useState("pending");
    const { get, loading, data, totalItems, setData, setTotalItems } = useRetroApi();
    const {
        open: openApproveDenyModal,
        modal: approveDenyModal
    } = useApproveDenyModal({ setData, setTotalItems, selectedStatus: selected });
    const { open: openDetailsModal, modal: detailsModal } = useDetailsModal();
    const columns = useColumns({ openDetailsModal, openApproveDenyModal, selectedStatus: selected });
    const { groups } = useGroups();
    const { peopleCustomFields } = useCustomFields();

    const table = useNewTable({
        data,
        columns,
        totalItems,
        enableRowSelection: false,
        loading,
        initialState: {
            columnVisibility: {
                "user_locations.locationId": false,
                "user_jobs.jobId": false,
                user_salaryOrHourly: false,
                user_calculationGroup: false,
                user_userGroups: false,
                user_employeeType: false,
                user_gender: false,
                user_race: false,
                user_employmentType: false,
            }
        }
    });

    const queryFilters = useQueryFilters(table);
    const config = useColumnFiltersConfig(table.getAllColumns());
    const filtersConfig = useMemo(() => [
        ...config,
        {
            name: "user.customFields",
            label: t("custom-fields"),
            children: (peopleCustomFields || []).map((customField) => {
                return {
                    name: customField.code,
                    label: customField.code,
                    type: customField.type === "select" ? "select" : "text",
                    component: customField.type === "select" ? (
                        <Select mode="multiple">
                            {customField.options.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    ) : undefined,
                };
            }),
        },
    ], [config, peopleCustomFields, t]);

    useEffect(() => {
        const controller = new AbortController();
        get(formatParams({ ...queryFilters, status: selected }), controller);
        return () => controller.abort();
    }, [get, queryFilters, selected]);


    useEffect(() => {
        if (groups?.length > 0) {
            table.setColumnVisibility((prev) => {
                const columnVisibility = { ...prev };
                groups.forEach((group) => {
                    const level = `${group.level}`.replace("_", "");
                    columnVisibility[`user_${level}`] = false;
                });
                return columnVisibility;
            });
        }
    }, [groups, table]);

    useEffect(() => {
        if (peopleCustomFields?.length > 0) {
            table.setColumnVisibility((prev) => {
                const columnVisibility = { ...prev };
                peopleCustomFields.forEach((field) => {
                    columnVisibility[`customFields_${field.code}`] = false;
                });
                return columnVisibility;
            });
        }
    }, [peopleCustomFields, table]);

    return (
        <Card className="h-100 w-100">
            <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">{t("retro-adjustment")}</h4>
                    <div className="d-flex gap-1 flex-row">
                        {status.map((item) => (
                            <span
                                key={item}
                                className={classNames(
                                    "border rounded px-3 py-1 cursor-pointer text-primary border-primary",
                                    selected === item && "bg-primary text-white font-weight-bold"
                                )}
                                onClick={() => setSelected(item)}
                            >
                                {t(item)}
                            </span>
                        ))}
                    </div>
                </div>
            </CardHeader>

            <div className="p-3 d-flex align-items-start">
                <Filters
                    config={filtersConfig}
                    filters={table.getState().columnFilters}
                    setFilters={table.setColumnFilters}
                />

                <div className="d-flex gap-x-2">
                    <ColumnToggle columns={table.getAllColumns()} />
                </div>
            </div>

            <NTable table={table} />

            {detailsModal}
            {approveDenyModal}
        </Card>
    );
}

export default RetroAdjustment;
